import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Container,
  Group,
  Loader,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  ArrowRight,
  Video,
  Volume2,
} from 'react-feather';
import { useNavigate, useParams } from 'react-router-dom';
import {
  waitingRoomApi,
  type WaitingRoomAttendee,
  type WaitingRoomNoShowState,
  type WaitingRoomPayload,
  type WaitingRoomPresence,
} from '../../../services/api/waitingRoom';
import { useFeatureFlag } from '../../../stores/featureFlags';
import { useSelectedCohortAccess } from '../../../stores/participantCohort';
import { useAuthStore } from '../../../stores/auth';
import {
  hasParticipantAiAck,
  setParticipantAiAck,
} from '../../../utils/participantAiAck';
import type { PresenceMode } from '../../../services/api/waitingRoom';
import { FeaturesDisabledPage } from '../../FeaturesDisabledPage';
import { AiDisclosureModal } from '../../../components/session/AiDisclosureModal';
import { JoinVideoCall } from '../../../components/session/JoinVideoCall';
import { getZoneCode } from '../../../utils/formatters';

/**
 * Session Waiting Room — Participant View (MED-804 / E3.2).
 *
 * Lands the participant in the waiting room before the session leader opens
 * the session. Handles the AC #1/#6 AI disclosure gate, polls the API at
 * `POLL_INTERVAL_MS` to keep presence and session status fresh, and renders
 * the AC #8–#17 layout (with the AC #15 / #16 mockup omissions). When the
 * session status flips to `in_progress`, navigates to the participant
 * session entry point.
 *
 * Polling-based presence (no websocket infra). The 4s cadence is chosen so a
 * single dropped poll does not flap a participant out of the 'present' state
 * (server threshold is 10s).
 */

const POLL_INTERVAL_MS = 4_000;

// MED-1048 — immersive shell tokens, matching the participant active-session
// view (#0E1B33) so entering the session is one continuous experience.
const SHELL_BG = '#0E1B33';
const CARD_MAX_WIDTH = 760;

/**
 * MED-1048 — the immersive dark shell shared by every waiting-room state
 * (normal / no-show / missed): a full-height dark backdrop with the status
 * line above a single centered card and the footer below it. The card stays
 * light (dark text) — only the page background is dark.
 */
function WaitingRoomShell({
  status,
  footer,
  children,
}: {
  status?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Box
      data-testid="waiting-room-shell"
      style={{
        minHeight: '100vh',
        backgroundColor: SHELL_BG,
        display: 'flex',
        flexDirection: 'column',
        padding: '24px 16px',
      }}
    >
      {status}
      <Box
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
        }}
      >
        <Card
          radius="lg"
          padding={0}
          style={{
            width: '100%',
            maxWidth: CARD_MAX_WIDTH,
            overflow: 'hidden',
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.35)',
          }}
        >
          {children}
        </Card>
      </Box>
      {footer}
    </Box>
  );
}

export interface SessionWaitingRoomPageProps {
  /**
   * MED-1048 — DEV-only static preview. Seeds the payload and bypasses the
   * feature-flag gate, the fetch/poll, and the navigation effects so the
   * immersive shell renders from an in-memory fixture (`/dev/waiting-room`).
   * Compiled out of production builds (gated at the route).
   */
  preview?: { payload: WaitingRoomPayload };
}

export function SessionWaitingRoomPage({
  preview,
}: SessionWaitingRoomPageProps = {}) {
  const isPreview = !!preview;
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const isFlagOn = useFeatureFlag('live_session_delivery');
  // MED-1307 (MDF-184): this page sits outside ParticipantLayout, so it applies
  // the cohort participant-features and app-access gate itself.
  const cohortAccess = useSelectedCohortAccess();

  const [payload, setPayload] = useState<WaitingRoomPayload | null>(
    preview?.payload ?? null
  );
  const [loading, setLoading] = useState(!preview);
  const [error, setError] = useState<string | null>(null);
  // MDF-242 - shared with the active page, so acknowledging here is not asked
  // again there, and a direct link to the active page still asks.
  const userId = useAuthStore(s => s.user?.id);
  const [aiAcknowledged, setAiAcknowledged] = useState(() =>
    hasParticipantAiAck(userId, sessionId)
  );
  // MDF-242: until the participant acknowledges, ask the server to record
  // presence only when the session needs no AI disclosure. The server holds the
  // AI settings, so an AI-off session is recorded on the first read. Read
  // through a ref so the polling callback always sends the current mode.
  const presenceModeRef = useRef<PresenceMode>(
    aiAcknowledged ? 'always' : 'unless_ai_disclosure'
  );
  presenceModeRef.current = aiAcknowledged ? 'always' : 'unless_ai_disclosure';
  // MED-1006 AC13b — once the session is declared `missed`, show the brief
  // notice then return to the dashboard. Sticky so a late poll can't bounce it.
  const [missedNotice, setMissedNotice] = useState(false);

  // ----- data fetch + polling lifecycle -----------------------------------

  const fetchPayload = useCallback(
    async (opts: { silent?: boolean } = {}) => {
      if (!sessionId) return;
      if (!opts.silent) setLoading(true);
      try {
        const result = await waitingRoomApi.get(sessionId, {
          presence: presenceModeRef.current,
        });
        setPayload(result);
        setError(null);
      } catch (err) {
        const e = err as Error & { status?: number };
        if (e?.status === 403) {
          setError('You are not enrolled in this session.');
        } else if (e?.status === 404) {
          setError('Session not found or unavailable.');
        } else if (!opts.silent) {
          setError(e?.message ?? 'Failed to load the waiting room.');
        }
      } finally {
        if (!opts.silent) setLoading(false);
      }
    },
    [sessionId]
  );

  useEffect(() => {
    if (isPreview) return;
    void fetchPayload();
  }, [fetchPayload, isPreview]);

  // Poll while mounted — keeps presence + session status fresh.
  useEffect(() => {
    if (isPreview || !sessionId) return;
    const intervalId = window.setInterval(() => {
      void fetchPayload({ silent: true });
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [sessionId, fetchPayload, isPreview]);

  // MED-1061 — is the CURRENT user the session's designated peer leader? Derived
  // entirely from the server payload (impersonation-safe — no auth store): a
  // peer-led session names its leader in `payload.leader`, and that same person
  // appears in the attendee roster flagged `is_current_participant`
  // (WaitingRoomService.buildAttendees). Memoized to a stable primitive so the
  // redirect effect below doesn't re-fire on every 4s poll (attendees is a fresh
  // array reference each fetch).
  const isCurrentUserSessionLeader = useMemo(
    () =>
      payload?.leader.role_label === 'PEER LEADER' &&
      (payload?.attendees.some(
        a => a.is_current_participant && a.role_label === 'PEER LEADER'
      ) ??
        false),
    [payload?.leader.role_label, payload?.attendees]
  );

  // ----- AI disclosure gate -----------------------------------------------

  // MED-876 follow-up: gate on both the modality flag AND the per-session
  // listening toggle. The modal's copy ("AI listening is active for this
  // session") is materially false when listening is off, so suppressing the
  // modal in that case is a correctness fix, not just a UX preference.
  const needsAiDisclosure =
    !!payload &&
    payload.ai_features_enabled &&
    payload.ai_listening_enabled &&
    !aiAcknowledged;

  const handleAcknowledgeAi = () => {
    setAiAcknowledged(true);
    if (isPreview) return;
    setParticipantAiAck(userId, sessionId);
    // MDF-242: record presence now rather than at the next 4s poll.
    presenceModeRef.current = 'always';
    void fetchPayload({ silent: true });
  };

  // Auto-redirect when the session opens. MED-1061: a returning PEER LEADER must
  // land on the cohort-scoped leader route (renders CoachActiveSessionPage, which
  // calls the coach active-state endpoint → is_session_leader=true → leader
  // controls), mirroring the first-join path in StartSessionButton and the
  // no-show "became_leader" branch below. Everyone else lands on the participant
  // active view. Previously this redirect was unconditional, so a rejoining peer
  // leader was demoted to a plain participant.
  useEffect(() => {
    if (isPreview) return;
    if (payload?.session.status !== 'in_progress') return;
    // MDF-242: a late joiner's first fetch already reads in_progress. Hold them
    // here until they acknowledge the AI disclosure; acknowledging clears
    // needsAiDisclosure and re-runs this effect, which then redirects.
    if (needsAiDisclosure) return;
    const destination = isCurrentUserSessionLeader
      ? `/participant/cohorts/${payload.session.cohort_id}/sessions/${payload.session.session_id}/active`
      : `/participant/sessions/${payload.session.session_id}/active`;
    navigate(destination, { replace: true });
  }, [
    payload?.session.status,
    payload?.session.session_id,
    payload?.session.cohort_id,
    isCurrentUserSessionLeader,
    needsAiDisclosure,
    navigate,
    isPreview,
  ]);

  // MED-1006 AC13b/IV9 — the no-show sweep flipped the session to `missed`.
  // Mirror the in_progress redirect: surface a brief notice in the waiting room,
  // then return the participant to the dashboard.
  useEffect(() => {
    if (payload?.session.status === 'missed') setMissedNotice(true);
  }, [payload?.session.status]);

  useEffect(() => {
    if (isPreview || !missedNotice) return;
    const id = window.setTimeout(
      () => navigate('/participant', { replace: true }),
      4000
    );
    return () => window.clearTimeout(id);
  }, [missedNotice, navigate, isPreview]);

  const handleReturnToPortal = () => {
    // AC #5 + IV4 — returning at the disclosure step navigates back to the
    // pre-session reflection page WITHOUT entering the waiting room.
    // MDF-242: no presence was recorded while the disclosure was open (the
    // reads ask for presence only when no disclosure is needed), so leaving
    // here leaves no attendance trace. The next time the participant returns,
    // the page re-fetches and asks again.
    if (sessionId) {
      navigate(`/participant/sessions/${sessionId}/reflection`, {
        replace: true,
      });
    } else {
      navigate('/participant');
    }
  };

  // ----- early returns -----------------------------------------------------

  // MED-1180 — defense-in-depth: also disable when the per-cohort LSD flag is
  // false on the returned payload (backend already 404s a non-LSD cohort).
  if (!isPreview && (!isFlagOn || payload?.live_session_delivery_enabled === false)) {
    return <FeaturesDisabledPage />;
  }

  if (!isPreview && cohortAccess !== 'enabled') {
    return <FeaturesDisabledPage reason={cohortAccess} />;
  }

  // MED-1006 AC13b — brief "missed" notice before the dashboard redirect.
  if (missedNotice) {
    return <MissedNoticeScreen />;
  }

  if (loading) {
    return (
      <Box
        style={{
          minHeight: '100vh',
          backgroundColor: SHELL_BG,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Loader color="white" />
      </Box>
    );
  }

  if (error || !payload) {
    return (
      <Box
        style={{
          minHeight: '100vh',
          backgroundColor: SHELL_BG,
          padding: '48px 16px',
        }}
      >
        <Container size="sm">
          <Alert
            icon={<AlertTriangle size={18} />}
            color="red"
            variant="filled"
            title="Waiting room unavailable"
          >
            {/* MED-1267 R2 — the default light Alert variant rendered dim,
                low-contrast text on the dark navy shell background (QA: "very
                hard to read"). A filled variant gives an opaque red box with
                white title/body text that reads clearly on the dark surface. */}
            <Text size="sm" c="white">
              {error ?? 'Unknown error.'}
            </Text>
          </Alert>
        </Container>
      </Box>
    );
  }

  // ----- main render -------------------------------------------------------

  return (
    <>
      <AiDisclosureModal
        opened={needsAiDisclosure}
        role={
          payload.leader.role_label === 'PEER LEADER'
            ? 'peer_leader'
            : 'participant'
        }
        onAcknowledge={handleAcknowledgeAi}
        // MED-1047 AC16 — the X returns the participant to their pre-session
        // page (matching the leader's dismiss), rather than acknowledging and
        // proceeding into the waiting room. Same destination as Return to portal.
        onDismiss={handleReturnToPortal}
        onReturnToPortal={handleReturnToPortal}
      />

      <WaitingRoomBody
        payload={payload}
        onRefresh={() => fetchPayload({ silent: true })}
      />
    </>
  );
}

// ===========================================================================
// MED-1006 AC13b — terminal "missed" notice shown briefly before redirect.
// ===========================================================================

function MissedNoticeScreen() {
  // MED-1048 AC11 — same immersive dark shell so there's no light/dark flip.
  return (
    <Box
      data-testid="waiting-room-shell"
      style={{
        minHeight: '100vh',
        backgroundColor: SHELL_BG,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
      }}
    >
      <Card
        radius="lg"
        p="xl"
        style={{
          width: '100%',
          maxWidth: 480,
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.35)',
        }}
      >
        <Stack gap="md" align="center">
          <ThemeIcon size={56} radius="md" color="warningAmber">
            <Clock size={28} />
          </ThemeIcon>
          <Title order={3} ta="center">
            This session was missed
          </Title>
          <Text c="dimmed" ta="center" size="sm" maw={420}>
            No one was able to start the session in time, so it will be
            rescheduled. We're returning you to your dashboard.
          </Text>
          <Loader size="sm" />
        </Stack>
      </Card>
    </Box>
  );
}

// ===========================================================================
// Body — only rendered after the AI disclosure has been resolved.
// ===========================================================================

function WaitingRoomBody({
  payload,
  onRefresh,
}: {
  payload: WaitingRoomPayload;
  onRefresh: () => void;
}) {
  const navigate = useNavigate();
  const { session, leader, attendees, reflection, no_show } = payload;

  // MED-1170 — normalize the neutral fallback to "your session leader" (matches
  // the no-show path's "Your session leader" and the AI disclosure modal). Kept
  // lowercase here since it appears mid-sentence in the hero/countdown copy.
  const leaderFirstName = leader.first_name || 'your session leader';
  const sessionId = session.session_id;

  const handleLeaveWaitingRoom = () => {
    navigate(`/participant/sessions/${sessionId}/reflection`);
  };

  const handleSubmitReflection = () => {
    navigate(`/participant/sessions/${sessionId}/reflection`);
  };

  // Shared status line (above the card) + footer (below it) on the dark shell.
  const statusBanner = (
    <TopStatusBanner
      scheduledStart={session.scheduled_start}
      timezone={session.timezone}
    />
  );
  const footer = (
    <WaitingRoomFooter
      groupName={session.group_name}
      durationMinutes={session.duration_minutes}
    />
  );

  // MED-1006 AC14 / MED-1244: the no-show state REPLACES the normal "{Leader}
  // will start shortly" hero + countdown + orientation bullets. The roster, base
  // actions (Join video call / Leave), and footer persist on every variant.
  //
  // MED-1244 — the no-show state now spans the WHOLE session window and has two
  // phases keyed off `volunteer_window_open`:
  //   • Phase 1 (false): confirmed-leader head start — "Waiting for your session
  //     leader", no volunteer CTA / readiness bullets (AC11).
  //   • Phase 2 (true):  head start elapsed, or no confirmed leader — volunteer
  //     CTA + readiness available through the estimated end (AC12).
  // Both phases show the plain "…scheduled to end at" line (AC13) and NO ticking
  // countdown to missed (AC10).
  if (no_show) {
    const volunteerOpen = no_show.volunteer_window_open;
    return (
      <WaitingRoomShell status={statusBanner} footer={footer}>
        <NoShowHeaderBand noShow={no_show} />
        <Stack gap="lg" p="xl">
          <AttendeesSection attendees={attendees} />

          {/* AC11 — Phase 1 shows only the waiting message + end line (below);
              readiness + volunteer CTA appear only once the window opens (AC12). */}
          {volunteerOpen && (
            <>
              <NoShowReadiness noShow={no_show} />

              <NoShowActionArea
                noShow={no_show}
                sessionId={sessionId}
                cohortId={session.cohort_id}
                onRefresh={onRefresh}
              />
            </>
          )}

          {/* AC13 — plain end-time line in BOTH phases (no ticking countdown). */}
          <SessionEndsLine
            sessionEndsAt={no_show.session_ends_at}
            timezone={session.timezone}
          />

          <Stack gap="sm">
            <JoinVideoCall
              href={session.meeting_link}
              variant="button"
              fullWidth
            />

            <Button
              variant="default"
              fullWidth
              onClick={handleLeaveWaitingRoom}
            >
              Leave waiting room
            </Button>
          </Stack>
        </Stack>
      </WaitingRoomShell>
    );
  }

  return (
    <WaitingRoomShell status={statusBanner} footer={footer}>
      <HeroBand leaderFirstName={leaderFirstName} />
      <Stack gap="lg" p="xl">
        <CountdownCard
          scheduledStart={session.scheduled_start}
          leaderFirstName={leaderFirstName}
          serverNow={payload.server_now}
        />

        <AttendeesSection attendees={attendees} />

        <OrientationBullets
          reflectionStatus={reflection.status}
          consideredForPrep={reflection.considered_for_prep}
          onSubmitReflection={handleSubmitReflection}
        />

        {/*
          MED-804 QA fix: the waiting room is the in-app front door for a
          scheduled session (the dashboard "Enter waiting room" CTA lands here).
          Surface the session's video call so the participant can join the Zoom
          AV channel without leaving the orientation surface. Not the AC #15
          "Test audio & video" button — this is the actual join link, rendered
          only when the session has a video URL.
        */}
        {/* MED-1112 — resolved meeting link via the shared component, so the
            waiting room and the live views share one destination + styling.
            Grouped tighter with "Leave waiting room" (the two actions read as a
            pair, separate from the orientation content above). */}
        <Stack gap="sm">
          <JoinVideoCall
            href={session.meeting_link}
            variant="button"
            fullWidth
          />

          {/*
            AC #15: Test audio & video button INTENTIONALLY NOT RENDERED.
            AC #16: "Message [CM] directly" link INTENTIONALLY NOT RENDERED.
            These omissions are deviations from the Jira ticket mockup, called
            out explicitly in stories/MED-804.md (Visual Specs section).
          */}
          <Button variant="default" fullWidth onClick={handleLeaveWaitingRoom}>
            Leave waiting room
          </Button>
        </Stack>
      </Stack>
    </WaitingRoomShell>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function TopStatusBanner({
  scheduledStart,
  timezone,
}: {
  scheduledStart: string | null;
  timezone: string;
}) {
  const formatted = useMemo(() => {
    if (!scheduledStart) return '';
    const d = new Date(scheduledStart);
    // MED-1389: name the zone, as the coach view does.
    try {
      const time = d.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: timezone,
      });
      return `${time} ${getZoneCode(timezone, d)}`;
    } catch {
      const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
      return `${time} ${getZoneCode(null, d)}`;
    }
  }, [scheduledStart, timezone]);

  // MED-1048 — sits on the dark shell above the card (no amber bar).
  return (
    <Group gap={8} justify="center" wrap="nowrap" mb="lg">
      <Box w={8} h={8} bg="warningAmber.5" style={{ borderRadius: '50%' }} />
      <Text size="sm" c="warningAmber.4" fw={500}>
        Waiting room · Session was scheduled to start at {formatted || 'soon'}
      </Text>
    </Group>
  );
}

// MED-1048 — the hero is now the green-tinted TOP BAND of the single card
// (full-bleed; the card's overflow:hidden clips it to the rounded corners).
function HeroBand({ leaderFirstName }: { leaderFirstName: string }) {
  return (
    <Box bg="medleyGreen.0" p="xl">
      <Stack gap="md" align="center">
        <ThemeIcon size={56} radius="md" color="medleyGreen">
          <Clock size={28} />
        </ThemeIcon>
        <Title order={2} c="medleyGreen.9" ta="center">
          You're in. {leaderFirstName} will start the session shortly.
        </Title>
        <Text c="medleyGreen.8" ta="center" size="sm" maw={500}>
          The waiting room keeps you here until {leaderFirstName} opens the
          session. Use the next few minutes to settle in.
        </Text>
      </Stack>
    </Box>
  );
}

function CountdownCard({
  scheduledStart,
  leaderFirstName,
  serverNow,
}: {
  scheduledStart: string | null;
  leaderFirstName: string;
  serverNow: string;
}) {
  // Anchor "now" to the server timestamp (so client clock skew doesn't matter)
  // and tick locally every second. When the polling refresh fires (every 4s)
  // serverNow updates and the anchor re-syncs.
  const anchorNow = useMemo(
    () => Date.parse(serverNow) || Date.now(),
    [serverNow]
  );
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setTick(t => t + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  const display = useMemo(() => {
    if (!scheduledStart) return 'Soon';
    const target = Date.parse(scheduledStart);
    if (!Number.isFinite(target)) return 'Soon';
    const elapsedSinceFetch = tick * 1000;
    const now = anchorNow + elapsedSinceFetch;
    const remainingMs = target - now;
    if (remainingMs <= 0) {
      return 'Starting soon';
    }
    const totalSeconds = Math.floor(remainingMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  }, [scheduledStart, tick, anchorNow]);

  return (
    <Card radius="md" p="md" withBorder>
      <Group justify="space-between" align="center">
        <Group gap="sm" align="center">
          <ThemeIcon variant="light" color="gray" size="md" radius="md">
            <Clock size={18} />
          </ThemeIcon>
          <Box>
            <Text fw={600} size="sm">
              Session starts in
            </Text>
            <Text size="xs" c="dimmed">
              {leaderFirstName} will open the room when they're ready
            </Text>
          </Box>
        </Group>
        <Badge
          size="xl"
          variant="outline"
          radius="md"
          color="medleyGreen"
          data-testid="waiting-room-countdown"
          // MED-1112 — tabular-nums so ticking digits keep an equal width and the
          // badge/layout doesn't jitter each second (matches the in-session timers).
          styles={{ label: { fontVariantNumeric: 'tabular-nums' } }}
        >
          {display}
        </Badge>
      </Group>
    </Card>
  );
}

function AttendeesSection({ attendees }: { attendees: WaitingRoomAttendee[] }) {
  return (
    <Stack gap="xs">
      <Text
        size="xs"
        fw={700}
        c="dimmed"
        tt="uppercase"
        style={{ letterSpacing: '0.5px' }}
      >
        Who's in the waiting room
      </Text>
      <Group gap="xs" wrap="wrap">
        {attendees.map(a => (
          <AttendeePill key={a.user_id} attendee={a} />
        ))}
      </Group>
    </Stack>
  );
}

const PRESENCE_LABEL: Record<WaitingRoomPresence, string> = {
  present: '',
  joining: '·joining…',
  not_yet_here: '·not yet here',
};

const ROLE_PALETTE: Array<'groupAlpha' | 'groupBeta' | 'groupGamma'> = [
  'groupAlpha',
  'groupBeta',
  'groupGamma',
];

function AttendeePill({ attendee }: { attendee: WaitingRoomAttendee }) {
  // Deterministic color per user_id so it's stable across polls.
  const colorIndex = useMemo(() => {
    let hash = 0;
    for (let i = 0; i < attendee.user_id.length; i++) {
      hash = (hash * 31 + attendee.user_id.charCodeAt(i)) >>> 0;
    }
    return hash % ROLE_PALETTE.length;
  }, [attendee.user_id]);

  const color = attendee.is_current_participant
    ? 'groupBeta'
    : ROLE_PALETTE[colorIndex];
  const muted = attendee.presence !== 'present';
  const tag = attendee.is_current_participant
    ? '·You'
    : PRESENCE_LABEL[attendee.presence];

  return (
    <Badge
      size="lg"
      radius="xl"
      color={color}
      variant={muted && !attendee.is_current_participant ? 'light' : 'filled'}
      style={{
        opacity:
          attendee.presence === 'not_yet_here' &&
          !attendee.is_current_participant
            ? 0.55
            : 1,
        textTransform: 'none',
      }}
      data-testid={`waiting-room-attendee-${attendee.user_id}`}
      data-presence={attendee.presence}
    >
      <Group gap={6} align="center" wrap="nowrap">
        <Text size="xs" fw={700} component="span">
          {attendee.initials}
        </Text>
        <Text size="xs" component="span">
          {attendee.display_name} {tag}
        </Text>
        {attendee.role_label && (
          <Text
            size="9px"
            fw={700}
            component="span"
            style={{ letterSpacing: '0.3px' }}
          >
            {attendee.role_label}
          </Text>
        )}
      </Group>
    </Badge>
  );
}

// ===========================================================================
// MED-1006 / E4.0f · MED-1244 — Day-of No-Show waiting-room variant.
//
// Visual styling follows the Figma "no-show" mock (amber-tinted header band,
// dark headline). Per the recorded mock-vs-AC deltas in stories/MED-1006.md, the
// copy OMITS the mock's "2-minute walkthrough" bullet and "Message {CM}
// directly" link (out of scope).
//
// MED-1244 — the amber ticking "countdown to missed" box is REMOVED (AC10). The
// header + action area render in two phases (see NoShowHeaderBand) and the copy
// reflects the whole-window model: the session isn't rescheduled until its
// scheduled end, and anyone can start it before then (AC14).
// ===========================================================================

function noShowLeaderName(noShow: WaitingRoomNoShowState): string {
  return noShow.leader_first_name || 'Your session leader';
}

// MED-1048 — the no-show header is the amber-tinted TOP BAND of the card.
// MED-1244 — two phases keyed off `volunteer_window_open`:
//   • Phase 1 (false): confirmed-leader head start — calm "Waiting for your
//     session leader / {Leader} is starting the session shortly" (AC11), no
//     imminent-reschedule framing.
//   • Phase 2 (true):  head start elapsed, or no confirmed leader — "leader
//     hasn't started / anyone can begin" (AC12/AC14).
function NoShowHeaderBand({ noShow }: { noShow: WaitingRoomNoShowState }) {
  const leaderName = noShowLeaderName(noShow);

  // AC11 — Phase 1: the confirmed leader still has the room to themselves; keep
  // the tone reassuring, no call to volunteer yet.
  if (!noShow.volunteer_window_open) {
    return (
      <Box bg="warningAmber.0" p="xl" data-testid="no-show-header">
        <Stack gap="md" align="center">
          <ThemeIcon size={56} radius="xl" color="warningAmber">
            <Clock size={28} />
          </ThemeIcon>
          <Title order={2} c="warningAmber.9" ta="center">
            Waiting for your session leader
          </Title>
          <Text c="warningAmber.9" ta="center" size="sm" maw={520}>
            {leaderName} is starting the session shortly.
          </Text>
        </Stack>
      </Box>
    );
  }

  // AC12/AC14 — Phase 2: two header scenarios keyed off WHY we're here. The
  // framing invites anyone to begin — with NO "will be rescheduled" pressure
  // (the whole-window model handles that; the reschedule only happens at the
  // scheduled end).
  const headline =
    noShow.header_scenario === 'leader_declined'
      ? 'Your planned session leader can’t lead today'
      : 'Your session leader hasn’t started yet';
  const reason =
    noShow.header_scenario === 'leader_declined'
      ? `${leaderName} let us know ahead of time.`
      : `${leaderName} hasn’t opened the room yet.`;

  return (
    <Box bg="warningAmber.0" p="xl" data-testid="no-show-header">
      <Stack gap="md" align="center">
        <ThemeIcon size={56} radius="xl" color="warningAmber">
          <Volume2 size={28} />
        </ThemeIcon>
        <Title order={2} c="warningAmber.9" ta="center">
          {headline}
        </Title>
        <Text c="warningAmber.9" ta="center" size="sm" maw={520}>
          {reason} When your group is ready, anyone can begin.
        </Text>
      </Stack>
    </Box>
  );
}

// MED-1244 AC13 — a low-key line that sets the outer expectation without a live
// countdown: the session can be started any time before its scheduled end,
// stated with the actual end time. Rendered in BOTH no-show phases; omitted when
// the estimated end is not computable (rare — see ESTIMATED_END_EXPR).
function SessionEndsLine({
  sessionEndsAt,
  timezone,
}: {
  sessionEndsAt: string | null;
  timezone: string;
}) {
  const formatted = useMemo(() => {
    if (!sessionEndsAt) return null;
    const d = new Date(sessionEndsAt);
    const opts: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };
    // MED-1389: name the zone, as the coach view does.
    try {
      const time = d.toLocaleTimeString(undefined, { ...opts, timeZone: timezone });
      return `${time} ${getZoneCode(timezone, d)}`;
    } catch {
      return `${d.toLocaleTimeString(undefined, opts)} ${getZoneCode(null, d)}`;
    }
  }, [sessionEndsAt, timezone]);

  if (!formatted) return null;

  return (
    <Text c="dimmed" size="sm" ta="center" data-testid="no-show-session-ends-line">
      You can start any time before this session is scheduled to end at{' '}
      {formatted}.
    </Text>
  );
}

function NoShowReadiness({ noShow }: { noShow: WaitingRoomNoShowState }) {
  // AC18 — readiness bullets shown to ELIGIBLE members only (volunteered /
  // no-response). Omitted for the passive variant.
  if (noShow.eligibility === 'passive') return null;

  return (
    <Stack gap="sm">
      <OrientationBullet
        title="The session materials are ready."
        body="Facilitation guide, agenda, and materials are set up."
      />
      {/* AC18 — AI bullet shown ONLY when AI is enabled for the session. */}
      {noShow.ai_features_enabled && (
        <OrientationBullet
          title="AI assistance during the session."
          body="The same support every leader gets."
        />
      )}
    </Stack>
  );
}

function NoShowActionArea({
  noShow,
  sessionId,
  cohortId,
  onRefresh,
}: {
  noShow: WaitingRoomNoShowState;
  sessionId: string;
  cohortId: string;
  onRefresh: () => void;
}) {
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);
  const [keeping, setKeeping] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // MED-1244 AC12 — the CTA stays available for the WHOLE remaining window
  // (through the estimated end), not a fixed countdown. There is no client-side
  // expiry; once the session's estimated end passes the sweep flips it to
  // `missed` and the status poll redirects, and the server rejects any late
  // claim in the interim.

  // Passive variant (declined the volunteer call OR chose "Keep waiting"): no
  // CTA, passive-voice status only (AC16). MED-1244 AC14 — reworded off the
  // imminent-reschedule framing to the whole-window model.
  if (noShow.eligibility === 'passive') {
    return (
      <Text
        c="dimmed"
        size="sm"
        ta="center"
        data-testid="no-show-passive-status"
      >
        We’re waiting for someone to start the session. It won’t be rescheduled
        until its scheduled end time.
      </Text>
    );
  }

  const ctaLabel = noShow.session_number
    ? `I’ll lead Session ${noShow.session_number}`
    : 'I’ll lead the session';

  const handleStart = async () => {
    setStarting(true);
    setActionError(null);
    try {
      const result = await waitingRoomApi.volunteerStart(sessionId);
      if (result.became_leader) {
        // AC11 — the volunteer gets the full leader experience.
        navigate(
          `/participant/cohorts/${cohortId}/sessions/${sessionId}/active`,
          { replace: true }
        );
      } else {
        // AC9 — another member won the race; land in the participant view.
        navigate(`/participant/sessions/${sessionId}/active`, {
          replace: true,
        });
      }
    } catch (err) {
      const e = err as Error & { status?: number };
      setActionError(
        e?.message ?? 'Could not start the session. Please try again.'
      );
      setStarting(false);
    }
  };

  const handleKeepWaiting = async () => {
    setKeeping(true);
    setActionError(null);
    try {
      await waitingRoomApi.keepWaiting(sessionId);
      // AC17 — persisted opt-out; refresh so the variant flips to passive.
      onRefresh();
    } catch {
      // Non-blocking — the next poll will reflect the opt-out if it succeeded.
      setKeeping(false);
    }
  };

  return (
    <Stack gap="xs" align="center">
      {/* AC16 — acknowledgment line for the "volunteered" variant only. */}
      {noShow.eligibility === 'volunteered' && (
        <Text size="sm" fw={500} ta="center">
          You volunteered earlier — ready to start?
        </Text>
      )}

      {actionError && (
        <Alert
          color="red"
          variant="light"
          w="100%"
          icon={<AlertTriangle size={16} />}
        >
          <Text size="sm">{actionError}</Text>
        </Alert>
      )}

      <Button
        fullWidth
        size="md"
        color="dark"
        leftSection={<Video size={16} />}
        onClick={handleStart}
        loading={starting}
        disabled={keeping}
        data-testid="no-show-start-cta"
      >
        {ctaLabel}
      </Button>

      <Button
        variant="subtle"
        color="gray"
        size="sm"
        onClick={handleKeepWaiting}
        loading={keeping}
        disabled={starting}
        data-testid="no-show-keep-waiting"
      >
        Keep waiting — let someone else step in
      </Button>
    </Stack>
  );
}

function OrientationBullets({
  reflectionStatus,
  consideredForPrep,
  onSubmitReflection,
}: {
  reflectionStatus: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';
  consideredForPrep: boolean;
  onSubmitReflection: () => void;
}) {
  // AC #11 — reflection orientation bullet, contextual on state.
  // First-draft copy; MED-870 owns final wording.
  const reflectionBullet: OrientationBulletProps = (() => {
    if (reflectionStatus === 'COMPLETED' && consideredForPrep) {
      return {
        title: 'Your reflection was considered.',
        body: 'Your responses were available before your session leader prepared today.',
      };
    }
    if (reflectionStatus === 'COMPLETED') {
      return {
        title: 'Your reflection is in.',
        body: 'Thanks for sharing — your responses are saved.',
      };
    }
    return {
      title: 'You can still submit your reflection.',
      body: "There's a few moments before the session starts. Submitting now still counts.",
      action: {
        label: 'Submit reflection',
        onClick: onSubmitReflection,
      },
    };
  })();

  // AC #13 — first-draft orientation bullets. Final copy from MED-870.
  const bullets: OrientationBulletProps[] = [
    reflectionBullet,
    {
      title: 'Mic and camera are good.',
      body: "Take a breath. You don't need to perform — just show up.",
    },
    {
      title: 'The session opens to a check-in.',
      body: '"How are you landing here today?" — keep your answer short and real.',
    },
  ];

  return (
    <Stack gap="md">
      {bullets.map((b, idx) => (
        <OrientationBullet key={idx} {...b} />
      ))}
    </Stack>
  );
}

interface OrientationBulletProps {
  title: string;
  body: string;
  action?: { label: string; onClick: () => void };
}

function OrientationBullet({ title, body, action }: OrientationBulletProps) {
  return (
    <Group gap="sm" align="flex-start" wrap="nowrap">
      {/* size matches the icon so the box doesn't center it low — it then
          aligns with the first line of the title text. */}
      <ThemeIcon variant="transparent" color="medleyGreen" size={20}>
        <CheckCircle size={20} />
      </ThemeIcon>
      <Stack gap={4} style={{ flex: 1 }}>
        <Text size="sm">
          <Text component="span" fw={700}>
            {title}{' '}
          </Text>
          {body}
        </Text>
        {action && (
          <Button
            variant="subtle"
            size="xs"
            color="medleyGreen"
            onClick={action.onClick}
            rightSection={<ArrowRight size={14} />}
            data-testid="waiting-room-reflection-cta"
            style={{ alignSelf: 'flex-start' }}
          >
            {action.label}
          </Button>
        )}
      </Stack>
    </Group>
  );
}

function WaitingRoomFooter({
  groupName,
  durationMinutes,
}: {
  groupName: string | null;
  durationMinutes: number | null;
}) {
  // MED-1048 — sits on the dark shell below the card (light text).
  return (
    <Box pt="lg">
      <Stack gap={2} align="center">
        <Text fw={700} size="sm" c="rgba(255, 255, 255, 0.92)">
          {groupName ?? '—'}
        </Text>
        {durationMinutes != null && (
          <Text size="xs" c="rgba(255, 255, 255, 0.55)">
            The session is scheduled for {durationMinutes} minutes
          </Text>
        )}
      </Stack>
    </Box>
  );
}
